import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { retryExchange, RetryExchangeOptions } from '@urql/exchange-retry';
import debug from 'debug';
import { twJoin } from 'tailwind-merge';
import {
  cacheExchange,
  Client as UrqlClient,
  Exchange,
  fetchExchange,
  makeOperation,
  mapExchange,
  Operation,
  Provider as UrqlProvider,
} from 'urql';

import environment from '@/environment';
import { getUser, UserResponse } from '@/hooks/admin/user';
import { useDevelopmentContext } from './DevelopmentContextProvider';

type GetHeaders = (operation: Operation) => Promise<Record<string, string>>;
/**
 * asyncHeaderExchange - A urql exchange that adds the headers from promise value.
 *
 * @param getHeaders {GetHeaders} - A function that returns a promise that resolves to a headers object.
 */
export const asyncHeaderExchange = (getHeaders: GetHeaders): Exchange =>
  mapExchange({
    async onOperation(operation) {
      if (typeof operation.context.fetchOptions === 'function') {
        operation.context.fetchOptions = operation.context.fetchOptions();
      }
      if (!operation.context.fetchOptions) {
        operation.context.fetchOptions = {
          headers: {},
        };
      }
      const headers = await getHeaders(operation);
      return makeOperation(operation.kind, operation, {
        ...operation.context,
        fetchOptions: {
          ...operation.context.fetchOptions,
          headers: {
            ...operation.context.fetchOptions.headers,
            ...headers,
          },
        },
      });
    },
  });

let userPromise: Promise<UserResponse> | null = null;
const generateUrqlClient = async (uri: string, onFetch: () => void) => {
  const retryOptions: RetryExchangeOptions = {
    initialDelayMs: 1000,
    maxDelayMs: 15000,
    randomDelay: true,
    maxNumberAttempts: 3,
    retryIf: (err) => !!err?.networkError,
  };

  return new UrqlClient({
    url: `${uri}/graphql`,
    exchanges: [
      cacheExchange,
      retryExchange(retryOptions),
      asyncHeaderExchange(async () => {
        userPromise = userPromise ?? getUser();
        const userId = (await userPromise.catch(() => null))?.user?.id.toString();
        return { 'x-dbt-user-id': userId ?? '' };
      }),
      fetchExchange,
    ],
    fetch: (...args) => {
      /**
       * This function gets called and then is quickly aborted by React renders
       * -- when this happens we do not end up making a network request, but we're
       * still counting it.
       *
       * This small timeout is an attempt to avoid overcounting.
       */
      const abortSignal = args[1]?.signal;
      setTimeout(() => {
        /**
         * We only issue introspection queries because of `urql/devtools`
         * which only runs in dev mode. These queries are not actually
         * issued in prod so we're going to avoid counting them.
         */
        const isIntrospectionQuery = args[1]?.body
          ?.toString()
          .includes('"operationName":"IntrospectionQuery"');

        if (!abortSignal?.aborted && !isIntrospectionQuery) {
          onFetch();
        }
      }, 50);

      if (!args[0]) {
        return Promise.reject(new Error('RequestInfo cannot be an empty string'));
      }
      return window.fetch(args[0] as RequestInfo, args[1]);
    },
    fetchOptions: () => {
      const fetchOptions: Record<string, any> = {
        headers: {
          Accept: '*/*', // This is critical to ensure that codex API returns a gzipped response.
          'x-dbt-partner-source': 'Explorer',
        },
      };
      return fetchOptions;
    },
  });
};

type ClientState = {
  publicDiscoveryUri: string | undefined;
  graphqlUri: string | undefined;
  appliedUpdatedAt: Date | undefined;
  definitionUpdatedAt: Date | undefined;
  numRequestsOnRoute: number;
  updateApplied: (updatedAt: Date) => void;
  updateDefinition: (updatedAt: Date) => void;
  resetClient: () => void;
};

const ClientContext = createContext<ClientState>({
  publicDiscoveryUri: undefined,
  graphqlUri: undefined,
  appliedUpdatedAt: undefined,
  definitionUpdatedAt: undefined,
  numRequestsOnRoute: 0,
  updateApplied: () => {},
  updateDefinition: () => {},
  resetClient: () => {},
});

interface DiscoveryUrqlUriProviderProps {
  uri: string;
}

export const useDiscoveryContext = () => {
  return useContext(ClientContext);
};

interface CacheMonitorParams {
  applied?: string | null | undefined;
  definition?: string | null | undefined;
}

const logger = debug('dbt:Explorer:urql');

export const useCacheMonitor = ({
  applied: appliedCacheKey,
  definition: definitionCacheKey,
}: CacheMonitorParams) => {
  const { appliedUpdatedAt, definitionUpdatedAt, updateApplied, updateDefinition } =
    useDiscoveryContext();

  useEffect(() => {
    if (!appliedCacheKey) return;
    const updatedAt = new Date(appliedCacheKey);
    const oldCacheKey = appliedUpdatedAt?.getTime();

    if (oldCacheKey && oldCacheKey < updatedAt.getTime()) {
      updateApplied(updatedAt);
      logger(`Update Applied: ${updatedAt}`);
    }
  }, [appliedUpdatedAt, appliedCacheKey, updateApplied]);

  useEffect(() => {
    if (!definitionCacheKey) return;
    const timestamp = new Date(definitionCacheKey);
    const oldCacheKey = definitionUpdatedAt?.getTime();

    if (oldCacheKey && oldCacheKey < timestamp.getTime()) {
      updateDefinition(timestamp);
      logger(`Update Definition: ${timestamp}`);
    }
  }, [definitionUpdatedAt, definitionCacheKey, updateDefinition]);
};

const DiscoveryUrqlUriProvider: React.FC<
  PropsWithChildren<DiscoveryUrqlUriProviderProps>
> = ({ uri, children }) => {
  const { isDev } = useDevelopmentContext();

  const [numRequestsOnRoute, setNumFetches] = useState(0);

  const makeUrqlClient = useCallback(() => {
    const onFetch = () => setNumFetches((numFetches) => numFetches + 1);
    return generateUrqlClient(uri, onFetch);
  }, [uri]);

  const [appliedUpdatedAt, updateAppliedUpdatedAt] = useState<Date>();
  const [definitionUpdatedAt, updateDefinitionUpdatedAt] = useState<Date>();

  const [client, setClient] = useState<UrqlClient>();

  const url = useLocation();
  useEffect(() => {
    setNumFetches(0);
  }, [url.pathname, url.search]);

  useEffect(() => {
    makeUrqlClient().then(setClient);
  }, [makeUrqlClient]);

  if (!client) return null;

  return (
    <ClientContext.Provider
      value={{
        publicDiscoveryUri: uri,
        graphqlUri: uri,
        numRequestsOnRoute,
        definitionUpdatedAt,
        appliedUpdatedAt,
        updateDefinition: (updatedAt) => {
          if (definitionUpdatedAt != null && definitionUpdatedAt !== updatedAt) {
            makeUrqlClient().then(setClient);
          }
          updateDefinitionUpdatedAt(updatedAt);
        },
        updateApplied: (updatedAt) => {
          if (appliedUpdatedAt != null && appliedUpdatedAt !== updatedAt) {
            makeUrqlClient().then(setClient);
          }
          updateAppliedUpdatedAt(updatedAt);
        },
        resetClient: () => makeUrqlClient().then(setClient),
      }}
    >
      <UrqlProvider value={client}>{children}</UrqlProvider>
      {isDev && environment.SHOULD_RENDER_NUM_REQUESTS && (
        <div
          className={twJoin(
            'absolute bottom-0 left-0 bg-bgSuccessMuted px-2',
            numRequestsOnRoute <= 3
              ? 'bg-bgSuccessMuted text-fgSuccess'
              : numRequestsOnRoute <= 5
                ? 'bg-bgWarningMuted text-fgWarning'
                : 'bg-bgDangerMuted text-fgDanger',
          )}
        >
          {numRequestsOnRoute} discovery requests
        </div>
      )}
    </ClientContext.Provider>
  );
};

interface DiscoveryUrqlProviderProps extends PropsWithChildren<{}> {
  uri: string | undefined;
}

const DiscoveryUrqlProvider: React.FC<
  PropsWithChildren<DiscoveryUrqlProviderProps>
> = ({ uri, children }) => {
  // If we don't have an incoming uri, use the monolith api gateway
  return (
    <DiscoveryUrqlUriProvider uri={uri ?? '/api/private/discovery/internal'}>
      {children}
    </DiscoveryUrqlUriProvider>
  );
};

export const useDiscoveryUrqlClient = () => useContext(ClientContext);

export default DiscoveryUrqlProvider;
